import React from "react";
import * as _ from "lodash";
import styled from "@emotion/styled";

type SortDirection = "asc" | "desc";

type SortBy = {
    key: string;
    direction: SortDirection;
};

type TransformObject<T extends object = any, U extends object = T> = (value: T, index?: number) => U;

function isNoDataValue(v: unknown): boolean {
    if (v == null) return true;
    if (v === "-") return true;
    if (v === Number.POSITIVE_INFINITY || v === Infinity) return true;
    return false;
}

/**
 * Sort so that "no data" values (Infinity, "-", null, undefined) always sort last
 * in both ascending and descending order.
 */
const sortCollection = (collection: any[], sortBy: SortBy) => {
    const { key, direction } = sortBy;
    const iteratee = (row: any) => {
        const v = _.get(row, key);
        if (isNoDataValue(v)) {
            return direction === "asc" ? Infinity : -Infinity;
        }
        // For string values, sort case-insensitively to avoid "Z" before "a"
        if (typeof v === "string") {
            return v.toLowerCase();
        }
        return v;
    };
    return _.orderBy(collection, iteratee, direction);
};

type SortIconProps = {
    keyName: string;
    sortBy: SortBy;
    setSortBy: React.Dispatch<React.SetStateAction<SortBy>>;
};

const SortIconTag = styled.i`
    position: absolute;
    top: 50%;
    font-size: ${(props: any) => props.size || 16}px;
    margin-left: 0.75rem;
    color: #828fab;
    transform: translateY(-50%);
    cursor: pointer;
    &.active {
        color: #00cbaf;
    }
`;

const SortIcon: React.FC<SortIconProps> = ({ sortBy, setSortBy, keyName }) => {
    const { direction } = sortBy;
    if (keyName !== sortBy.key) {
        return (
            <SortIconTag
                className="material-icons"
                onClick={() => {
                    setSortBy({ key: keyName, direction: "desc" });
                }}
            >
                unfold_more
            </SortIconTag>
        );
    }

    return direction === "asc" ? (
        <SortIconTag
            className="material-icons active"
            onClick={() => {
                setSortBy({ key: keyName, direction: "desc" });
            }}
        >
            expand_less
        </SortIconTag>
    ) : (
        <SortIconTag
            className="material-icons active"
            onClick={() => {
                setSortBy({ key: keyName, direction: "asc" });
            }}
        >
            expand_more
        </SortIconTag>
    );
};

type SortedCollectionWithButton<T> = {
    /** Sorted collection. */
    data: T[];

    /** Key used to sort the collection. */
    key: string;

    /** Direction that the collection was sorted. */
    direction: SortDirection;

    /** Function that can be called to return an icon button that can be clicked to sort the collection. */
    sortIcon: (
        /** Key to sort the collection by. */
        key: string
    ) => JSX.Element;
};

/**
 * Returns a sorted collection and a button that can be clicked to sort the collection.
 * @param collection Collection to be sorted.
 * @param transform Function to transform the collection before sorting.
 * @returns Returns a {@link SortedCollectionWithButton}.
 */
const useCollectSort = <T extends object = any, U extends object = T>(
    collection?: T[],
    transform?: TransformObject<T, U>,
    options?: {
        /** Whether to reset the sort when the collection changes. Default is true. */
        resetOnCollectionChange?: boolean;
        /** Initial sort by. Default is the first key of the first object in the collection with ascending direction. */
        initialSortBy?: SortBy;
    },
): SortedCollectionWithButton<U> => {
    const fixedOptions: any = React.useMemo(() => ({
        initialSortBy: options?.initialSortBy ?? {
            key: "",
            direction: "asc",
        },
        resetOnCollectionChange: options?.resetOnCollectionChange ?? true,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), []);

    const [sortBy, setSortBy] = React.useState<SortBy>(fixedOptions.initialSortBy);

    React.useEffect(() => {
        if (fixedOptions.resetOnCollectionChange) {
            _.size(collection) && setSortBy(fixedOptions.initialSortBy);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collection]);

    const transformedCollection = transform && collection
        ? collection.map((item, index) => transform(item, index))
        : collection;
    const data = transformedCollection ? sortCollection(transformedCollection, sortBy) : [];

    const sortIcon = (key: string) => (
        <SortIcon keyName={key} sortBy={sortBy} setSortBy={setSortBy} />
    );

    return { data, key: sortBy.key, direction: sortBy.direction, sortIcon };
};

export default useCollectSort;
